import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Divider,
  Stack,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import { propertyApi, formatErrorMessage } from '../../services/BaseApiService.jsx';
import ReportPrintLayout from '../Report/ReportPrintLayout';
import ReportLayoutContext from '../../contexts/ReportLayoutContext.jsx';
import { ReportConfigProvider } from '../../contexts/ReportConfigContext.jsx';

const DENSITY_OPTIONS = [
  { value: '', label: 'Padrão (herdar)' },
  { value: 'compact', label: 'Compacto' },
  { value: 'normal', label: 'Normal' },
];

export default function ReportLayoutAdmin() {
  const [layout, setLayout] = useState(null);
  const [draftLayout, setDraftLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  const [scope, setScope] = useState('client');
  const [previewFileId, setPreviewFileId] = useState('');
  const [previewSnapshot, setPreviewSnapshot] = useState(null);
  const [previewDocs, setPreviewDocs] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const activeLayout = isEditing ? draftLayout : layout;

  const previewLayoutValue = useMemo(() => {
    if (!activeLayout) return null;

    const page = activeLayout.page || null;
    const sections = activeLayout.sections || {};

    const getPageSettings = () => page || null;

    const getSectionLayout = (sectionKey) => {
      if (!sectionKey) return null;
      const section = sections[sectionKey];
      if (!section || typeof section !== 'object') return null;
      return section;
    };

    const getWidgetLayout = (sectionKey, widgetKey) => {
      if (!sectionKey || !widgetKey) return null;
      const section = sections[sectionKey];
      if (!section || typeof section !== 'object') return null;
      const widgets = section.widgets || {};
      const widget = widgets[widgetKey];
      if (!widget || typeof widget !== 'object') return null;
      return widget;
    };

    return {
      layout: activeLayout,
      loading: false,
      error: null,
      getPageSettings,
      getSectionLayout,
      getWidgetLayout,
    };
  }, [activeLayout]);

  useEffect(() => {
    let isActive = true;

    async function loadLayout() {
      setLoading(true);
      setError(null);
      try {
        const resp = await propertyApi.get('/report-layout', {
          params: { layout_key: 'print_default' },
        });
        if (!isActive) return;
        const data = resp?.data || null;
        if (data && typeof data === 'object') {
          setLayout(data);
          setDraftLayout(data);
          setIsEditing(false);
        } else {
          setLayout(null);
          setDraftLayout(null);
          setError('Layout de relatório inválido retornado pelo backend.');
        }
      } catch (e) {
        if (!isActive) return;
        setLayout(null);
        setDraftLayout(null);
        setError(formatErrorMessage(e) || 'Erro ao carregar layout de relatório.');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    }

    loadLayout();

    return () => {
      isActive = false;
    };
  }, []);

  const handleLoadPreviewExample = async () => {
    const trimmed = previewFileId.trim();
    if (!trimmed) {
      setPreviewError('Informe um file_id para carregar o relatório de exemplo.');
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewSnapshot(null);
    setPreviewDocs(null);

    try {
      const encoded = encodeURIComponent(trimmed);

      const stateResp = await fetch(`/api/v1/state/${encoded}`, {
        headers: { accept: 'application/json' },
      });

      if (!stateResp.ok) {
        throw new Error(`Erro HTTP ${stateResp.status}`);
      }

      const data = await stateResp.json();
      const snap = data && typeof data === 'object' && 'snapshot' in data ? data.snapshot : data;
      setPreviewSnapshot(snap || null);

      try {
        const docsResp = await fetch(`/api/v1/docs/${encoded}`, {
          headers: baseHeaders,
        });

        if (docsResp.ok) {
          const docsData = await docsResp.json();
          setPreviewDocs(docsData || null);
        }
      } catch (docsErr) {
        console.error('[ReportLayoutAdmin] Erro ao carregar documentos para preview:', docsErr);
      }
    } catch (e) {
      console.error('[ReportLayoutAdmin] Erro ao carregar relatório de exemplo para preview:', e);
      setPreviewSnapshot(null);
      setPreviewDocs(null);
      setPreviewError(
        formatErrorMessage(e) || 'Erro ao carregar relatório de exemplo para preview.',
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const sectionKeys = useMemo(() => {
    if (!activeLayout || !activeLayout.sections) return [];
    return Object.keys(activeLayout.sections).sort();
  }, [activeLayout]);

  useEffect(() => {
    if (!selectedSectionKey && sectionKeys.length > 0) {
      setSelectedSectionKey(sectionKeys[0]);
    }
  }, [sectionKeys, selectedSectionKey]);

  const handleReload = async () => {
    setLoading(true);
    setError(null);
    setIsEditing(false);
    try {
      const resp = await propertyApi.get('/report-layout', {
        params: { layout_key: 'print_default' },
      });
      const data = resp?.data || null;
      if (data && typeof data === 'object') {
        setLayout(data);
        setDraftLayout(data);
      } else {
        setLayout(null);
        setDraftLayout(null);
        setError('Layout de relatório inválido retornado pelo backend.');
      }
    } catch (e) {
      setLayout(null);
      setDraftLayout(null);
      setError(formatErrorMessage(e) || 'Erro ao recarregar layout de relatório.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (!layout) return;
    try {
      const cloned = JSON.parse(JSON.stringify(layout));
      setDraftLayout(cloned);
      setIsEditing(true);
      setError(null);
    } catch (_e) {
      setError('Erro ao iniciar edição do layout (falha ao clonar objeto).');
    }
  };

  const handleCancelEdit = () => {
    if (!layout) {
      setDraftLayout(null);
    } else {
      try {
        const cloned = JSON.parse(JSON.stringify(layout));
        setDraftLayout(cloned);
      } catch (_e) {
        setDraftLayout(layout);
      }
    }
    setIsEditing(false);
    setError(null);
  };

  const updateDraftLayout = (updater) => {
    if (!isEditing) return;
    setDraftLayout((prev) => {
      if (!prev) return prev;
      return updater(prev);
    });
  };

  const handleSave = async () => {
    if (!draftLayout) return;
    setSaving(true);
    setError(null);
    try {
      const layoutKey = draftLayout.layoutKey || 'print_default';
      const resp = await propertyApi.put('/report-layout', draftLayout, {
        params: {
          layout_key: layoutKey,
          scope,
        },
      });
      const data = resp?.data || draftLayout;
      setLayout(data);
      setDraftLayout(data);
      setIsEditing(false);
    } catch (e) {
      setError(formatErrorMessage(e) || 'Erro ao salvar layout de relatório.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSection = (sectionKey, changes) => {
    updateDraftLayout((prev) => {
      const next = { ...prev };
      const sections = { ...(next.sections || {}) };
      const current = { ...(sections[sectionKey] || { key: sectionKey, widgets: {} }) };
      Object.assign(current, changes);
      sections[sectionKey] = current;
      next.sections = sections;
      return next;
    });
  };

  const handleUpdateWidget = (sectionKey, widgetKey, changes) => {
    updateDraftLayout((prev) => {
      const next = { ...prev };
      const sections = { ...(next.sections || {}) };
      const section = { ...(sections[sectionKey] || { key: sectionKey, widgets: {} }) };
      const widgets = { ...(section.widgets || {}) };
      const widget = { ...(widgets[widgetKey] || { key: widgetKey }) };
      Object.assign(widget, changes);
      widgets[widgetKey] = widget;
      section.widgets = widgets;
      sections[sectionKey] = section;
      next.sections = sections;
      return next;
    });
  };

  const handleNumericChange = (value, fallback = 0) => {
    if (value === '' || value === null || value === undefined) return fallback;
    const parsed = Number(String(value).replace(',', '.'));
    if (Number.isNaN(parsed)) return fallback;
    return parsed;
  };

  if (loading && !activeLayout) {
    return (
      <Box sx={{ p: 2, mt: { xs: 7, sm: 8 }, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!activeLayout) {
    return (
      <Box sx={{ p: 2, mt: { xs: 7, sm: 8 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body1">Nenhum layout de relatório carregado.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={handleReload}>
          Recarregar layout
        </Button>
      </Box>
    );
  }

  const page = activeLayout.page || null;
  const sections = activeLayout.sections || {};
  const selectedSection = selectedSectionKey ? sections[selectedSectionKey] || null : null;

  const previewDocsFiles = Array.isArray(previewDocs?.files) ? previewDocs.files : [];
  const previewUserName = previewSnapshot?._metadata?.requested_by || '';

  return (
    <Box sx={{ p: 2, mt: { xs: 7, sm: 8 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">Administração - Layout de Impressão</Typography>
          <Typography variant="body2" color="text.secondary">
            Ajuste a ordem, visibilidade, densidade e colunas de seções e widgets do relatório
            impresso.
          </Typography>
          {page && (
            <Typography variant="caption" color="text.secondary">
              layoutKey: {activeLayout.layoutKey || 'print_default'}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Escopo"
            size="small"
            select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="client">Cliente (via JWT)</MenuItem>
            <MenuItem value="global">Global (padrão)</MenuItem>
          </TextField>
          <Button variant="outlined" onClick={handleReload} disabled={loading || saving}>
            Recarregar
          </Button>
          {!isEditing ? (
            <Button variant="contained" onClick={handleStartEdit} disabled={loading || saving}>
              Editar
            </Button>
          ) : (
            <>
              <Button variant="text" onClick={handleCancelEdit} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar layout'}
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid
        container
        spacing={2}
        sx={{
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
        }}
      >
        <Grid item xs={12} md={2}>
          <Paper sx={{ p: 1, height: '100%', minHeight: 360 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Seções
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {sectionKeys.map((key) => {
                const section = sections[key] || {};
                const visible = section.visible !== false;
                return (
                  <ListItemButton
                    key={key}
                    selected={selectedSectionKey === key}
                    onClick={() => setSelectedSectionKey(key)}
                  >
                    <ListItemText primary={key} secondary={visible ? 'Visível' : 'Oculta'} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* Coluna central: Seção + Widgets */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1, height: '100%', minHeight: 220, overflow: 'auto' }}>
            {!selectedSection ? (
              <Typography variant="body2" color="text.secondary">
                Selecione uma seção para editar.
              </Typography>
            ) : (
              <>
                <Box sx={{ mb: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="subtitle1">
                      Seção: {selectedSection.key || selectedSectionKey}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedSection.visible !== false}
                          disabled={!isEditing}
                          onChange={(e) =>
                            handleUpdateSection(selectedSectionKey, {
                              visible: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Seção visível"
                    />
                  </Stack>
                  <Grid container spacing={0.75}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Ordem"
                        size="small"
                        type="number"
                        fullWidth
                        value={selectedSection.order ?? 0}
                        disabled={!isEditing}
                        onChange={(e) =>
                          handleUpdateSection(selectedSectionKey, {
                            order: handleNumericChange(e.target.value, 0),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        label="Densidade"
                        size="small"
                        select
                        fullWidth
                        value={selectedSection.density || ''}
                        disabled={!isEditing}
                        onChange={(e) =>
                          handleUpdateSection(selectedSectionKey, {
                            density: e.target.value || null,
                          })
                        }
                      >
                        {DENSITY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value || 'default'} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedSection.pageBreakBefore === true}
                            disabled={!isEditing}
                            onChange={(e) =>
                              handleUpdateSection(selectedSectionKey, {
                                pageBreakBefore: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Quebra antes"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={selectedSection.pageBreakAfter === true}
                            disabled={!isEditing}
                            onChange={(e) =>
                              handleUpdateSection(selectedSectionKey, {
                                pageBreakAfter: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Quebra depois"
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 1 }} />

                {selectedSection.widgets && Object.keys(selectedSection.widgets).length > 0 ? (
                  Object.entries(selectedSection.widgets || {}).map(([widgetKey, widget]) => (
                    <Box
                      key={widgetKey}
                      sx={{
                        mb: 0.5,
                        p: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        columnGap: 0.75,
                        rowGap: 0.25,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mr: 1 }}>
                        Widget: {widgetKey}
                      </Typography>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={widget.visible !== false}
                            disabled={!isEditing}
                            onChange={(e) =>
                              handleUpdateWidget(selectedSectionKey, widgetKey, {
                                visible: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Visível"
                        sx={{ mr: 1 }}
                      />

                      <TextField
                        label="Ordem"
                        size="small"
                        type="number"
                        value={widget.order ?? 0}
                        disabled={!isEditing}
                        onChange={(e) =>
                          handleUpdateWidget(selectedSectionKey, widgetKey, {
                            order: handleNumericChange(e.target.value, 0),
                          })
                        }
                        sx={{ width: 80 }}
                      />

                      <TextField
                        label="Col. início"
                        size="small"
                        type="number"
                        value={widget.colStart ?? 1}
                        disabled={!isEditing}
                        onChange={(e) =>
                          handleUpdateWidget(selectedSectionKey, widgetKey, {
                            colStart: handleNumericChange(e.target.value, 1),
                          })
                        }
                        sx={{ width: 90 }}
                      />

                      <TextField
                        label="Largura (cols)"
                        size="small"
                        type="number"
                        value={widget.colSpan ?? 12}
                        disabled={!isEditing}
                        onChange={(e) =>
                          handleUpdateWidget(selectedSectionKey, widgetKey, {
                            colSpan: handleNumericChange(e.target.value, 12),
                          })
                        }
                        sx={{ width: 110 }}
                      />

                      <TextField
                        label="Densidade"
                        size="small"
                        select
                        value={widget.density || ''}
                        disabled={!isEditing}
                        onChange={(e) =>
                          handleUpdateWidget(selectedSectionKey, widgetKey, {
                            density: e.target.value || null,
                          })
                        }
                        sx={{ width: 140 }}
                      >
                        {DENSITY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value || 'default'} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={widget.avoidPageBreakInside === true}
                            disabled={!isEditing}
                            onChange={(e) =>
                              handleUpdateWidget(selectedSectionKey, widgetKey, {
                                avoidPageBreakInside: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Evitar quebra dentro"
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Esta seção não possui widgets configurados.
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Coluna direita: Preview da seção (A4) */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 1.5, height: '100%', minHeight: 360, overflow: 'auto' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Preview da seção (A4)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Use um file_id real para carregar um relatório de exemplo e visualizar apenas a seção
              selecionada com o layout atual.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ mb: 1 }}
              alignItems="flex-start"
            >
              <TextField
                label="file_id para preview"
                size="small"
                value={previewFileId}
                onChange={(e) => setPreviewFileId(e.target.value)}
                sx={{ minWidth: 220, maxWidth: 260 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleLoadPreviewExample}
                disabled={previewLoading || !previewFileId.trim()}
              >
                {previewLoading ? 'Carregando...' : 'Carregar exemplo'}
              </Button>
            </Stack>

            {previewError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {previewError}
              </Alert>
            )}

            {previewLoading && !previewSnapshot && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {previewSnapshot && previewLayoutValue && (
              <Box
                sx={{
                  mt: 1,
                  p: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: '#f8fafc',
                  maxHeight: '70vh',
                  overflow: 'auto',
                }}
              >
                <ReportConfigProvider>
                  <ReportLayoutContext.Provider value={previewLayoutValue}>
                    <ReportPrintLayout
                      snapshot={previewSnapshot}
                      userName={previewUserName}
                      docsFiles={previewDocsFiles}
                      mapSnapshots={{}}
                      filterSectionKey={selectedSectionKey}
                    />
                  </ReportLayoutContext.Provider>
                </ReportConfigProvider>
              </Box>
            )}

            {!previewSnapshot && !previewLoading && !previewError && (
              <Typography variant="body2" color="text.secondary">
                Informe um file_id e clique em "Carregar exemplo" para visualizar esta seção em
                formato de página A4.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
